/**
 * Task 309A — «أداء المحتوى العضوي», a section of the client Reports page.
 *
 * It renders `organic_performance_snapshots`, the pre-computed aggregate. It
 * never fetches a raw daily fact — client users have no RLS branch on those
 * tables at all — so what arrives is already bounded: the totals, ≤ 92 series
 * points, the breakdowns and ≤ 10 ranked posts.
 *
 * THREE HONESTY RULES DRIVE THE WHOLE RENDER, and each one exists because the
 * obvious shortcut would be a lie:
 *
 *   1. `status: 'unavailable'` is not an empty period. Rendering zeros for
 *      "we have not computed this yet" would invent a month of silence, so the
 *      unavailable branch returns an explicit empty state and never a KPI grid.
 *
 *   2. A `null` total is not `0`. `metricState()` splits it three ways: a
 *      number renders as a number; a null whose metric IS in
 *      `supported_post_metrics` renders «لا توجد بيانات»; a null whose metric
 *      is NOT renders «غير مدعوم». Two different facts, two different words.
 *
 *   3. A cumulative total is not a period total. When
 *      `temporality.post_metrics` is `cumulative` (Instagram and TikTok post
 *      insights restate a lifetime figure every day) the KPI label switches to
 *      «تراكمي حتى تاريخه» and the trend is labelled a cumulative reading. The
 *      numbers are correct either way — the aggregate already reduced each
 *      series by its own rule — but the SENTENCE they belong to is different.
 *
 * A synthetic snapshot can never reach a client user (the RLS branch requires
 * `contains_synthetic = false`), so the banner below is for agency viewers, who
 * can see one and must not mistake it for real performance.
 *
 * No chart library, no decorative visuals: the trend is a table, like every
 * other numeric surface in this app.
 */

import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { getClientOrganicPerformance } from '../../api/endpoints';
import type {
  ClientOrganicPerformanceData,
  OrganicBreakdownEntryWire,
  OrganicPerformanceSnapshotWire,
  OrganicPeriodKindWire,
  OrganicPlatformWire,
  OrganicRankedPostWire,
} from '../../api/types';
import { isApiError } from '../../api/client';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';

/* ── bidi (Task 303-B QA pattern) ──────────────────────────────────────── */

const BIDI_CONTROLS =
  // U+200E LRM, U+200F RLM, U+061C ALM; U+202A–U+202E embeddings/overrides;
  // U+2066–U+2069 isolates. Written as escapes on purpose — the literals are
  // invisible, and an invisible character in a character class is not reviewable.
  /[\u200E\u200F\u061C\u202A-\u202E\u2066-\u2069]/g;

export function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

/**
 * A Latin run whose direction is carried by MARKUP. `bdi` isolates it so the
 * surrounding Arabic cannot reorder it; the explicit `dir="ltr"` sets the base
 * direction inside, because a bare `<bdi>` guesses from the first STRONG
 * character and digits are not strong.
 */
export function LtrRun({ children }: { readonly children: ReactNode }): JSX.Element {
  return <bdi dir="ltr">{children}</bdi>;
}

/** `YYYY-MM-DD` exactly — a calendar date, with no time and no zone. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatOrganicDateAr(value: string | null): string {
  if (!value) return '—';
  // `new Date('2026-09-04')` is UTC midnight, and formatting it in a browser
  // west of UTC renders the 3rd. `metric_date`, `period_start`, `period_end`
  // and `data_through_date` are all calendar dates, not instants, so they are
  // built as LOCAL midnight and never cross a day boundary on the way to the
  // screen.
  const dateOnly = DATE_ONLY.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return stripBidiControls(value);
  try {
    return stripBidiControls(
      new Intl.DateTimeFormat('ar-SA', {
        dateStyle: 'medium',
        calendar: 'gregory',
        numberingSystem: 'latn',
      }).format(parsed),
    );
  } catch {
    return parsed.toISOString().slice(0, 10);
  }
}

/* ── Arabic vocabulary ─────────────────────────────────────────────────── */

export const ORGANIC_PERIOD_LABEL_AR: Readonly<Record<OrganicPeriodKindWire, string>> = {
  last_7_days: 'آخر ٧ أيام',
  last_30_days: 'آخر ٣٠ يوماً',
  last_90_days: 'آخر ٩٠ يوماً',
};

export const ORGANIC_PERIOD_ORDER: ReadonlyArray<OrganicPeriodKindWire> = [
  'last_7_days',
  'last_30_days',
  'last_90_days',
];

export const ORGANIC_PLATFORM_LABEL_AR: Readonly<Record<OrganicPlatformWire, string>> = {
  instagram: 'إنستقرام',
  facebook: 'فيسبوك',
  tiktok: 'تيك توك',
  snapchat: 'سناب شات',
};

export const ORGANIC_FORMAT_LABEL_AR: Readonly<Record<string, string>> = {
  instagram_feed: 'منشور إنستقرام',
  instagram_story: 'ستوري إنستقرام',
  instagram_reel: 'ريلز إنستقرام',
  facebook_post: 'منشور فيسبوك',
  facebook_story: 'ستوري فيسبوك',
  tiktok_video: 'فيديو تيك توك',
  snapchat_story: 'ستوري سناب شات',
  unclassified: 'غير مصنّف',
};

/** The ten post metrics, in the order the KPI grid reads them. */
/** The five account-grain metrics, labelled like the post ones. */
export const ORGANIC_ACCOUNT_METRIC_LABEL_AR: Readonly<Record<string, string>> = {
  followers_count: 'المتابعون',
  /* NET, not "new". The column is signed — an account that lost followers
     reports a loss, and the boundary accepts a negative because the sign is
     the information. "New followers: -5" is a label contradicting its own
     value. */
  new_followers: 'صافي تغير المتابعين',
  impressions: 'مرات ظهور الحساب',
  reach: 'وصول الحساب',
  profile_views: 'زيارات الملف',
};

export const ORGANIC_METRIC_LABEL_AR: Readonly<Record<string, string>> = {
  impressions: 'مرات الظهور',
  reach: 'الوصول',
  engagements: 'التفاعلات',
  reactions: 'الإعجابات',
  comments: 'التعليقات',
  shares: 'المشاركات',
  saves: 'الحفظ',
  video_views: 'مشاهدات الفيديو',
  video_watch_seconds: 'ثواني المشاهدة',
  clicks: 'النقرات',
};

export const ORGANIC_KPI_ORDER: ReadonlyArray<string> = [
  'impressions',
  'reach',
  'engagements',
  'reactions',
  'comments',
  'shares',
  'saves',
  'video_views',
  'video_watch_seconds',
  'clicks',
];

/** What the engagement-rate denominator was. */
export const ORGANIC_RATE_BASIS_LABEL_AR: Readonly<Record<string, string>> = {
  reach: 'من الوصول',
  impressions: 'من مرات الظهور',
  followers: 'من المتابعين',
  unavailable: 'غير متاح',
};

/** Where the engagement numerator came from. */
export const ORGANIC_ENGAGEMENTS_SOURCE_LABEL_AR: Readonly<Record<string, string>> = {
  provider_total: 'إجمالي من المنصة',
  derived_components: 'مجموع مكوّنات التفاعل',
  unavailable: 'غير متاح',
};

/**
 * What the totals MEAN, which the cohort decides and the number cannot show.
 *
 *   interval   — measurements dated inside the period: the period's own
 *                earnings, whenever the post was published.
 *   cumulative — posts PUBLISHED inside the period, at their latest reading:
 *                a lifetime figure for this period's posts, NOT the period's
 *                earnings, and not comparable period-over-period the way an
 *                interval total is.
 *
 * The Arabic says which, every time a total is shown.
 */
export const ORGANIC_TEMPORALITY_LABEL_AR: Readonly<Record<string, string>> = {
  interval: 'قياسات خلال الفترة',
  cumulative: 'إجمالي تراكمي حتى تاريخه لمنشورات الفترة',
  mixed: 'مختلط: تراكمي لمنشورات الفترة + قياسات خلال الفترة',
  not_applicable: 'لا ينطبق',
};

/**
 * The same axis, at the ACCOUNT grain.
 *
 * The post labels name a post COHORT - "for the period's posts" - and account
 * metrics have no cohort at all: followers and account reach describe the
 * ACCOUNT on a date. Reusing the post phrasing on the audience card told the
 * reader those figures were scoped to the period's posts, which is not a
 * smaller truth but a different one.
 */
export const ORGANIC_ACCOUNT_TEMPORALITY_LABEL_AR: Readonly<Record<string, string>> = {
  interval: 'قياسات خلال الفترة',
  cumulative: 'إجمالي تراكمي حتى تاريخه للحساب',
  mixed: 'مختلط: تراكمي للحساب + قياسات خلال الفترة',
  not_applicable: 'لا ينطبق',
};

const NO_DATA_AR = 'لا توجد بيانات';
const UNSUPPORTED_AR = 'غير مدعوم';

/* ── null vs zero vs unsupported ───────────────────────────────────────── */

export type MetricState =
  | { readonly kind: 'value'; readonly value: number }
  | { readonly kind: 'no_data' }
  | { readonly kind: 'unsupported' };

/**
 * The three-way split rule 2 exists for.
 *
 * A number is a measurement, including `0`. A null is an absence, and
 * `supported` is what says WHICH absence: a platform that cannot report saves
 * is a different fact from a platform that can and has not yet.
 */
export function metricState(
  value: number | null | undefined,
  metric: string,
  supported: ReadonlyArray<string>,
): MetricState {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'value', value };
  }
  return supported.includes(metric) ? { kind: 'no_data' } : { kind: 'unsupported' };
}

/**
 * "based on N of M" — but only when N is actually less than M.
 *
 * This is the whole of the completeness contract on the read side. The
 * aggregate publishes a sum of what was reported and states how much of the
 * population it covers; the reader is never shown a partial figure dressed as
 * a complete one, and never shown an absence where a real measurement exists.
 *
 * Silent when coverage is complete, because a note on every number is a note
 * nobody reads.
 */
export function coverageNote(
  covered: number | undefined,
  population: number | undefined,
): string | null {
  if (typeof covered !== 'number' || typeof population !== 'number') return null;
  if (population <= 0 || covered >= population) return null;
  return `\u0645\u0628\u0646\u064a \u0639\u0644\u0649 ${formatCount(covered)} \u0645\u0646 ${formatCount(population)}`;
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatRate(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value * 100)}%`;
}

/* ── state ─────────────────────────────────────────────────────────────── */

type SectionState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: ClientOrganicPerformanceData }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

export interface OrganicPerformanceSectionProps {
  readonly clientId: string;
}

export function OrganicPerformanceSection(props: OrganicPerformanceSectionProps): JSX.Element {
  const { clientId } = props;
  const [periodKind, setPeriodKind] = useState<OrganicPeriodKindWire>('last_30_days');
  const [platform, setPlatform] = useState<OrganicPlatformWire | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<SectionState>({ status: 'idle' });

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    getClientOrganicPerformance({ clientId, periodKind, platform, signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, periodKind, platform, reloadKey]);

  // The platform filter offers only what a snapshot actually exists for, so it
  // can never ask the server for a scope it already knows is empty.
  const availablePlatforms = state.status === 'ready' ? state.data.available_platforms : [];

  // …and a selection that survives a change of period or client must be
  // dropped. Changing the period can retire a platform from the options while
  // `platform` still holds it: the `<select>` then renders with no matching
  // option — which browsers show as the FIRST option, "كل المنصات" — while
  // every request keeps asking for the platform that is no longer offered. The
  // control and the data disagree, and the control is the one the reader
  // believes.
  useEffect(() => {
    if (state.status !== 'ready') return;
    if (platform === null) return;
    if (state.data.available_platforms.includes(platform)) return;
    setPlatform(null);
  }, [state, platform]);

  return (
    <section aria-label="أداء المحتوى العضوي" className="stack-md">
      <div className="sec-title">
        <h2>أداء المحتوى العضوي</h2>
        <span className="meta">قياسات المنشورات على حسابات التواصل المرتبطة</span>
      </div>

      <div className="card">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <label>
            <span className="lbl">الفترة</span>
            <select
              value={periodKind}
              aria-label="فترة القياس"
              onChange={(e) => setPeriodKind(e.target.value as OrganicPeriodKindWire)}
            >
              {ORGANIC_PERIOD_ORDER.map((k) => (
                <option key={k} value={k}>
                  {ORGANIC_PERIOD_LABEL_AR[k]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="lbl">المنصة</span>
            <select
              value={platform ?? ''}
              aria-label="منصة القياس"
              onChange={(e) =>
                setPlatform(e.target.value === '' ? null : (e.target.value as OrganicPlatformWire))
              }
            >
              <option value="">كل المنصات</option>
              {availablePlatforms.map((p) => (
                <option key={p} value={p}>
                  {ORGANIC_PLATFORM_LABEL_AR[p]}
                </option>
              ))}
            </select>
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setReloadKey((n) => n + 1)}
            >
              تحديث
            </button>
          </div>
        </div>
      </div>

      {state.status === 'loading' || state.status === 'idle' ? (
        <LoadingState variant="inline" title="جاري تحميل أداء المحتوى العضوي…" />
      ) : null}

      {state.status === 'not-found' ? (
        <NotFoundState
          title="القياسات غير متاحة"
          description="هذا العميل غير متاح لحسابك أو غير موجود."
        />
      ) : null}

      {state.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل أداء المحتوى العضوي"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {state.status === 'ready' ? <OrganicPerformanceBody data={state.data} /> : null}
    </section>
  );
}

/**
 * The rendered body, exported and PROP-DRIVEN on purpose.
 *
 * The fetching lives in the parent, so this renders under `react-dom/server`
 * with no DOM, no network and no mocks — which is the only way to assert what a
 * reader actually SEES. A source grep proves a string is in the file; it cannot
 * prove the branch guarding it is the right way round.
 */
export function OrganicPerformanceBody(props: {
  readonly data: ClientOrganicPerformanceData;
}): JSX.Element {
  const { data } = props;

  // Rule 1: "not computed" is its own outcome. No KPI grid, no zeros.
  if (data.status !== 'ready' || !data.snapshot) {
    return (
      <EmptyState
        title="لا توجد قياسات محسوبة بعد"
        description={
          /* Each reason says what was actually established, and no more. The
             endpoint reads SNAPSHOTS: it learns that no fresh row exists for a
             scope, never that no account is linked to it. Diagnosing a missing
             connection from a missing snapshot sends the reader to the
             connections page to fix something that may not be broken. */
          data.reason === 'platform_not_covered'
            ? 'لا توجد قياسات محسوبة لهذه المنصة في هذه الفترة. قد يكون الحساب مرتبطًا ولم تُحسب قياساته بعد.'
            : data.reason === 'snapshot_stale'
              ? 'آخر قياسات محسوبة تخصّ فترة أقدم، ولم يجرِ تحديثها لهذه الفترة بعد.'
              : 'لم تُحسب قياسات لهذا العميل بعد. إن لم يكن هناك حساب تواصل مرتبط، فلن تُحسب.'
        }
      />
    );
  }

  const snap: OrganicPerformanceSnapshotWire = data.snapshot;
  const supported = snap.completeness.supported_post_metrics;
  const cumulative = snap.temporality.post_metrics;
  const totalsLabel = ORGANIC_TEMPORALITY_LABEL_AR[cumulative] ?? cumulative;
  const rate = formatRate(snap.engagement_rate);
  const byPlatform = snap.breakdowns.by_platform ?? [];
  const byFormat = snap.breakdowns.by_format ?? [];

  return (
    <div className="stack-md">
      {snap.source.contains_synthetic ? (
        <div className="banner banner-error" role="status">
          بيانات تجريبية اصطناعية — لا تُستخدم في أي تقرير للعميل.
        </div>
      ) : null}

      <div className="card">
        <div className="sec-title">
          <h3>المصدر والتحديث</h3>
          {/* NOT `totalsLabel`. That phrase describes the post-totals cohort
              ("cumulative to date, for posts published in the period"), and on
              a freshness card it reads as a claim about the freshness numbers
              themselves. This card reports coverage, so it is labelled with
              the period it covers. */}
          <span className="meta">
            <LtrRun>{`${snap.period.start} → ${snap.period.end}`}</LtrRun>
          </span>
        </div>
        <table className="tbl">
          <tbody>
            <tr>
              <td className="name">الفترة</td>
              <td className="num">
                <LtrRun>{formatOrganicDateAr(snap.period.start)}</LtrRun>
                {' — '}
                <LtrRun>{formatOrganicDateAr(snap.period.end)}</LtrRun>
              </td>
            </tr>
            <tr>
              <td className="name">حسابات مشمولة</td>
              <td className="num">{formatCount(snap.source.account_count)}</td>
            </tr>
            <tr>
              <td className="name">آخر احتساب</td>
              <td className="num">
                <LtrRun>{formatOrganicDateAr(snap.freshness.computed_at)}</LtrRun>
              </td>
            </tr>
            <tr>
              <td className="name">البيانات حتى</td>
              <td className="num">
                {snap.freshness.data_through_date ? (
                  <LtrRun>{formatOrganicDateAr(snap.freshness.data_through_date)}</LtrRun>
                ) : (
                  NO_DATA_AR
                )}
              </td>
            </tr>
            <tr>
              <td className="name">آخر مزامنة ناجحة</td>
              <td className="num">
                {snap.freshness.last_successful_sync_at ? (
                  <LtrRun>{formatOrganicDateAr(snap.freshness.last_successful_sync_at)}</LtrRun>
                ) : (
                  NO_DATA_AR
                )}
              </td>
            </tr>
            <tr>
              <td className="name">اكتمال الأيام</td>
              <td className="num">
                {formatCount(snap.completeness.days_with_data)} /{' '}
                {formatCount(snap.completeness.expected_days)}
              </td>
            </tr>
            <tr>
              <td className="name">منشورات لها قياسات</td>
              <td className="num">
                {formatCount(snap.completeness.posts_with_metrics)} /{' '}
                {formatCount(snap.completeness.posts_in_period)}
              </td>
            </tr>
            {snap.completeness.posts_excluded_undated > 0 ? (
              <tr>
                <td className="name">منشورات مستبعدة بلا تاريخ نشر</td>
                <td className="num">{formatCount(snap.completeness.posts_excluded_undated)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {/* What the two absences MEAN, and what a partial period does
            instead. This paragraph used to say a partial sum was withheld —
            the behaviour Decision §10.3 retired. The aggregate publishes the
            sum and states its coverage; a page still promising to withhold it
            teaches the reader to distrust the number they are looking at. */}
        <p className="tiny" style={{ marginTop: 8 }}>
          «{UNSUPPORTED_AR}» تعني أن المنصة لا توفّر هذا القياس، و«{NO_DATA_AR}» تعني أنها توفّره
          ولم تصل أي قيمة للفترة. أمّا إذا أبلغ جزء من منشورات الفترة — أو جزء من الحسابات في النطاق
          — فيُعرض مجموع ما ورد فعلاً، ومكتوب تحته «مبني على N من M» ليوضّح أنه مجموع ملاحَظ لا
          إجمالي كامل. لا يُعرض صفر في أي من حالتَي الغياب.
        </p>
        {snap.completeness.posts_excluded_undated > 0 ? (
          <p className="tiny">
            منشورات بلا تاريخ نشر لا يمكن نسبتها إلى فترة، لذلك استُبعدت من الإجماليات أعلاه.
          </p>
        ) : null}
      </div>

      <div>
        <div className="sec-title">
          <h3>المؤشرات</h3>
          <span className="meta">{totalsLabel}</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
          }}
        >
          {ORGANIC_KPI_ORDER.map((metric) => {
            const st = metricState(
              snap.totals[metric as keyof typeof snap.totals],
              metric,
              supported,
            );
            return (
              <div key={metric} className="card kpi" data-metric={metric}>
                <span className="lbl">{ORGANIC_METRIC_LABEL_AR[metric] ?? metric}</span>
                <span className="v" data-metric-value={metric}>
                  {st.kind === 'value' ? formatCount(st.value) : null}
                  {st.kind === 'no_data' ? <span className="tiny">{NO_DATA_AR}</span> : null}
                  {st.kind === 'unsupported' ? (
                    <span className="tiny">{UNSUPPORTED_AR}</span>
                  ) : null}
                </span>
                {st.kind === 'value' ? (
                  <span className="tiny" data-metric-coverage={metric}>
                    {/* Optional at every step. A snapshot written before this
                        column existed, or an endpoint that does not project
                        it, must cost the reader a coverage NOTE — not the
                        whole report. */}
                    {coverageNote(
                      snap.completeness.metric_coverage?.post?.[metric],
                      snap.completeness.metric_coverage?.post_population,
                    )}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="sec-title">
          <h3>معدل التفاعل والجمهور</h3>
          <span className="meta">
            {(ORGANIC_ENGAGEMENTS_SOURCE_LABEL_AR[snap.engagements_source] ??
              snap.engagements_source) +
              ' · ' +
              (ORGANIC_ACCOUNT_TEMPORALITY_LABEL_AR[snap.temporality.account_metrics] ??
                snap.temporality.account_metrics)}
          </span>
        </div>
        <table className="tbl">
          <tbody>
            <tr>
              {/* The engagement rate is POST-derived: its numerator comes from
                  the post fold and its denominator from whichever basis the
                  fold chose. The card's caption describes the ACCOUNT grain, so
                  the rate carries its own. */}
              <td className="name">
                معدل التفاعل{' '}
                <span className="tiny">
                  (
                  {ORGANIC_TEMPORALITY_LABEL_AR[snap.temporality.post_metrics] ??
                    snap.temporality.post_metrics}
                  )
                </span>
              </td>
              <td className="num">
                {rate === null ? (
                  <span className="tiny">{NO_DATA_AR}</span>
                ) : (
                  <>
                    <LtrRun>{rate}</LtrRun>{' '}
                    <span className="tiny">
                      {ORGANIC_RATE_BASIS_LABEL_AR[snap.engagement_rate_basis] ??
                        snap.engagement_rate_basis}
                    </span>
                  </>
                )}
              </td>
            </tr>
            <tr>
              {/* FIRST OBSERVED, not "start of period". With one mid-window reading
                  the same value is both ends, and "beginning" / "end" of a period
                  that was never measured at its edges is a claim the data does
                  not support. The contract already said first/last reporting
                  day; only the label disagreed. */}
              <td className="name">المتابعون في أول يوم مقاس</td>
              <td className="num">
                {accountMetricCell(snap.audience.followers_start, 'followers_count', snap)}
                <div className="tiny">مستوى وليس مجموعًا</div>
              </td>
            </tr>
            <tr>
              <td className="name">المتابعون في آخر يوم مقاس</td>
              <td className="num">
                {accountMetricCell(snap.audience.followers_end, 'followers_count', snap)}
              </td>
            </tr>
            <tr>
              {/* Not "between the two measured days": with a single follower
                  observation SQL emits it as BOTH ends, and the delta is a zero
                  that was never observed between anything. */}
              <td className="name">صافي التغير بين أول وآخر يوم مقاس</td>
              <td className="num">
                {accountMetricCell(snap.audience.followers_delta, 'followers_count', snap, true)}
              </td>
            </tr>
            <tr>
              <td className="name">{ORGANIC_ACCOUNT_METRIC_LABEL_AR['new_followers']}</td>
              <td className="num">
                {accountMetricCell(snap.audience.new_followers, 'new_followers', snap, true)}
                {/* `new_followers` is a daily GAIN and `followers_count` a
                    LEVEL, whatever the account declares — the card's
                    temporality caption describes neither. Each says what it
                    is, so a cumulative account cannot label a daily gain as
                    a lifetime total. */}
                <div className="tiny">مجموع التغير اليومي خلال الفترة (قد يكون سالبًا)</div>
              </td>
            </tr>
            <tr>
              <td className="name">{ORGANIC_ACCOUNT_METRIC_LABEL_AR['impressions']}</td>
              <td className="num">
                {accountMetricCell(snap.account_totals.impressions, 'impressions', snap)}
              </td>
            </tr>
            <tr>
              <td className="name">{ORGANIC_ACCOUNT_METRIC_LABEL_AR['reach']}</td>
              <td className="num">{accountMetricCell(snap.account_totals.reach, 'reach', snap)}</td>
            </tr>
            <tr>
              <td className="name">{ORGANIC_ACCOUNT_METRIC_LABEL_AR['profile_views']}</td>
              <td className="num">
                {accountMetricCell(snap.account_totals.profile_views, 'profile_views', snap)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 12,
        }}
      >
        <BreakdownCard
          title="حسب المنصة"
          keyHeader="المنصة"
          unit={totalsLabel}
          supported={supported}
          rows={byPlatform}
          labelOf={(k) => ORGANIC_PLATFORM_LABEL_AR[k as OrganicPlatformWire] ?? k}
        />
        <BreakdownCard
          title="حسب الشكل"
          keyHeader="الشكل"
          unit={totalsLabel}
          supported={supported}
          rows={byFormat}
          labelOf={(k) => ORGANIC_FORMAT_LABEL_AR[k] ?? k}
        />
      </div>

      <RankedPostsCard
        title="أفضل المنشورات"
        unit={totalsLabel}
        supported={supported}
        posts={snap.top_posts}
      />
      <RankedPostsCard
        title="أضعف المنشورات"
        unit={totalsLabel}
        supported={supported}
        posts={snap.bottom_posts}
      />

      <div className="card">
        <div className="sec-title">
          <h3>التسلسل اليومي</h3>
          <span className="meta">{totalsLabel}</span>
        </div>
        {snap.daily_series.length === 0 ? (
          <p className="tiny">{NO_DATA_AR}.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th className="name">اليوم</th>
                  <th className="num">منشورات</th>
                  <th className="num">{ORGANIC_METRIC_LABEL_AR['impressions']}</th>
                  <th className="num">{ORGANIC_METRIC_LABEL_AR['reach']}</th>
                  <th className="num">{ORGANIC_METRIC_LABEL_AR['engagements']}</th>
                </tr>
              </thead>
              <tbody>
                {snap.daily_series.map((point) => (
                  <tr key={point.date} data-day={point.date}>
                    <td className="name">
                      <LtrRun>{formatOrganicDateAr(point.date)}</LtrRun>
                    </td>
                    <td className="num">{formatCount(point.posts_with_data)}</td>
                    <td className="num">
                      {postMetricCell(point.impressions, 'impressions', supported)}
                    </td>
                    <td className="num">{postMetricCell(point.reach, 'reach', supported)}</td>
                    <td className="num">
                      {postMetricCell(point.engagements, 'engagements', supported)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BreakdownCard(props: {
  readonly title: string;
  /** The temporality label. These totals mean whatever the KPIs mean. */
  readonly unit: string;
  /** The post metrics every account in scope can supply — the intersection. */
  readonly supported: ReadonlyArray<string>;
  /** The first column's header — what the rows are broken down BY. */
  readonly keyHeader: string;
  readonly rows: ReadonlyArray<OrganicBreakdownEntryWire>;
  readonly labelOf: (key: string) => string;
}): JSX.Element {
  return (
    <div className="card">
      <div className="sec-title">
        <h3>{props.title}</h3>
        <span className="meta">
          منشورات · {ORGANIC_METRIC_LABEL_AR['engagements']} · {props.unit}
        </span>
      </div>
      {props.rows.length === 0 ? (
        <p className="tiny">{NO_DATA_AR}.</p>
      ) : (
        <table className="tbl">
          {/* A column is not explained by the sentence above the table. Screen
              readers announce cells against their header, and without one the
              three numbers in each row are unlabelled. */}
          <caption className="tiny" style={{ captionSide: 'top', textAlign: 'start' }}>
            {props.title}
          </caption>
          <thead>
            <tr>
              <th className="name" scope="col">
                {props.keyHeader}
              </th>
              <th className="num" scope="col">
                منشورات
              </th>
              <th className="num" scope="col">
                {ORGANIC_METRIC_LABEL_AR['engagements']}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <tr key={row.key} data-breakdown={row.key}>
                <td className="name">{props.labelOf(row.key)}</td>
                <td className="num">{formatCount(row.posts)}</td>
                <td className="num">
                  {postMetricCell(row.engagements, 'engagements', props.supported)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RankedPostsCard(props: {
  readonly title: string;
  readonly unit: string;
  readonly supported: ReadonlyArray<string>;
  readonly posts: ReadonlyArray<OrganicRankedPostWire>;
}): JSX.Element {
  return (
    <div className="card">
      <div className="sec-title">
        <h3>{props.title}</h3>
        <span className="meta">حتى ١٠ منشورات · {props.unit}</span>
      </div>
      {props.posts.length === 0 ? (
        <p className="tiny">{NO_DATA_AR}.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th className="name">الشكل</th>
                <th className="name">تاريخ النشر</th>
                <th className="num">{ORGANIC_METRIC_LABEL_AR['impressions']}</th>
                <th className="num">{ORGANIC_METRIC_LABEL_AR['reach']}</th>
                <th className="num">{ORGANIC_METRIC_LABEL_AR['engagements']}</th>
                <th className="name">الرابط</th>
              </tr>
            </thead>
            <tbody>
              {props.posts.map((post) => (
                <tr key={post.organic_post_id} data-post={post.organic_post_id}>
                  <td className="name">
                    {ORGANIC_FORMAT_LABEL_AR[post.post_format ?? 'unclassified'] ??
                      post.post_format ??
                      ORGANIC_FORMAT_LABEL_AR['unclassified']}
                  </td>
                  <td className="name">
                    <LtrRun>{formatOrganicDateAr(post.published_at)}</LtrRun>
                  </td>
                  <td className="num">
                    {postMetricCell(post.impressions, 'impressions', props.supported)}
                  </td>
                  <td className="num">{postMetricCell(post.reach, 'reach', props.supported)}</td>
                  <td className="num">
                    {postMetricCell(post.engagements, 'engagements', props.supported)}
                  </td>
                  <td className="name">
                    {post.permalink ? (
                      <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                        عرض المنشور
                      </a>
                    ) : (
                      <span className="tiny">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * An account-grain cell, held to the same three-way rule as a KPI: a number is
 * a measurement, a null on a SUPPORTED metric is «لا توجد بيانات», and a null
 * on one the platform does not report is «غير مدعوم». The audience card
 * previously called every null "no data", which told a reader that a metric
 * their platform cannot produce was merely late.
 *
 * …and to the same COVERAGE rule, which it previously was not. The aggregate
 * publishes an observed sum over the accounts that reported, and states how
 * many of the scope that was; the KPI grid printed that sentence and this cell
 * did not, so a follower total resting on one of two linked accounts rendered
 * as an unqualified figure for the whole scope. Coverage is what makes a
 * published partial honest — a partial published WITHOUT it is worse than one
 * withheld, because it reads as complete.
 */
function accountMetricCell(
  value: number | null | undefined,
  metric: string,
  snap: OrganicPerformanceSnapshotWire,
  signed = false,
): ReactNode {
  const st = metricState(value, metric, snap.completeness.supported_account_metrics);
  if (st.kind !== 'value') {
    return <span className="tiny">{st.kind === 'no_data' ? NO_DATA_AR : UNSUPPORTED_AR}</span>;
  }
  return (
    <>
      {signed ? nullableSignedCount(st.value) : formatCount(st.value)}
      {/* Optional at every step, exactly as in the KPI grid: a snapshot
          written before the column existed must cost the reader a NOTE, not
          the audience card. */}
      <div className="tiny" data-account-metric-coverage={metric}>
        {coverageNote(
          snap.completeness.metric_coverage?.account?.[metric],
          snap.completeness.metric_coverage?.account_population,
        )}
      </div>
    </>
  );
}

/**
 * A post-grain cell, under the same three-way rule as the KPI grid.
 *
 * This used to render every null as «لا توجد بيانات», which told a reader that
 * a metric their platform CANNOT produce was merely late — the exact sentence
 * the KPI grid was fixed not to say. A breakdown row and a leaderboard cell are
 * read as literally as a KPI, so they answer the same way.
 */
function postMetricCell(
  value: number | null | undefined,
  metric: string,
  supported: ReadonlyArray<string>,
): ReactNode {
  const st = metricState(value, metric, supported);
  if (st.kind === 'value') return formatCount(st.value);
  return <span className="tiny">{st.kind === 'no_data' ? NO_DATA_AR : UNSUPPORTED_AR}</span>;
}

/** A follower delta may legitimately be negative; the sign is the information. */
function nullableSignedCount(value: number | null | undefined): ReactNode {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return <span className="tiny">{NO_DATA_AR}</span>;
  }
  const sign = value > 0 ? '+' : '';
  return <LtrRun>{`${sign}${formatCount(value)}`}</LtrRun>;
}

export default OrganicPerformanceSection;
